/* eslint-disable max-len */
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { animateScroll } from 'react-scroll'
import { Slot, SlotsModuleChildProps, useSlotFavorite } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'

import { useOutcomeFreeSpinGameIds, useSlots } from 'modules/SlotsRoot/redefinitions/hooks'
import { SlotPlace } from 'modules/SlotsRoot/redefinitions/types'
import { ISlotViewItem } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'
import {
  buildFilterCategoryNames,
  filterSlotsByFavorite,
  filterSlotsBySelectedCategory,
  getAllCategorySlots,
} from 'modules/SlotsRoot/utils/filterCategories'
import { filterFreeSpinSlotsByOutcome } from 'modules/SlotsRoot/utils/outcomeFreeSpin'
import { isCasinoDefaultFilters, sortCasinoPopularSlotsFirst } from 'modules/SlotsRoot/utils/sortCasinoPopularSlots'

import { selectLocaleDict } from 'features/locale/selectors'

import { Spinner } from 'components/Spinner'

import { AllSlotsList } from './AllSlotsList/AllSlotsList'

import './SlotsLayoutContent.scss'

const FilterBlock = React.lazy(() => import('components/slots/FilterBlock/FilterBlock'))

type SlotsLayoutContentProps = SlotsModuleChildProps<SlotPlace> & {
  modal?: boolean
  addFrame?: ((slot: Slot) => () => void) | ((slot: ISlotViewItem) => () => void)
}

const b = block('slots-layout-content')

const SlotsLayoutContent: React.FC<SlotsLayoutContentProps> = ({ place, filters, modal, addFrame }) => {
  const locale = useSelector(selectLocaleDict, shallowEqual).slots

  const { favoriteSlotIds } = useSlotFavorite()
  const isFreeSpinPlace = place === 'free-spin'
  const { outcomeFreeSpinGameIds, isOutcomeFreeSpinLoading } = useOutcomeFreeSpinGameIds(isFreeSpinPlace)

  const {
    _slots: { list: allSlots, isLoading },
    _categories: { list: categories, listWithSlots },
    _providers: { list: providers },
  } = useSlots({ place, withoutRequests: false, filters, isNeedAllCategories: true })

  const filterCategories = useMemo(() => buildFilterCategoryNames(categories), [categories])

  const sortedSlots = useMemo(() => {
    const selectedCategories = filters.selectedCategories || []

    if (isFreeSpinPlace) {
      const catalogFreeSpinSlots = getAllCategorySlots(listWithSlots)
      const freeSpinSlots = filterFreeSpinSlotsByOutcome(catalogFreeSpinSlots, outcomeFreeSpinGameIds)

      return filterSlotsByFavorite(freeSpinSlots, selectedCategories, favoriteSlotIds)
    }

    const filteredSlots = filterSlotsBySelectedCategory(
      allSlots,
      listWithSlots,
      selectedCategories,
      favoriteSlotIds,
    )

    if (place !== 'casino' || !isCasinoDefaultFilters(filters)) {
      return filteredSlots
    }

    return sortCasinoPopularSlotsFirst(filteredSlots)
  }, [allSlots, favoriteSlotIds, filters, isFreeSpinPlace, listWithSlots, outcomeFreeSpinGameIds, place])

  useEffect(() => {
    animateScroll.scrollToTop()
  }, [])

  return (
    <div className={b({ modal })}>
      <Spinner isLoading={isLoading || isOutcomeFreeSpinLoading} />

      <FilterBlock
        locale={locale}
        providers={providers}
        categories={filterCategories}
        filters={filters}
      />

      <div className={b('wrapper')}>
        <div className={b('container')}>
          <AllSlotsList
            modal={modal}
            addFrame={addFrame}
            getGameInfo={undefined}
            slots={sortedSlots}
            filters={filters}
          />
        </div>
      </div>
    </div>
  )
}

export default SlotsLayoutContent
